import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import type { SupabaseClientLike } from './people';

const PREF_KEYS = ['preferences'] as const;

async function fetchPreferences(supabase: SupabaseClientLike): Promise<Record<string, unknown>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const res = await fetch('/functions/v1/settings/preferences', {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<Record<string, unknown>>;
}

async function patchPreference(
  supabase: SupabaseClientLike,
  key: string,
  value: unknown,
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const res = await fetch(`/functions/v1/settings/preferences/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw new Error(await res.text());
}

/**
 * Returns [value, setValue] for a single preference key.
 * setValue debounces 500ms then PATCHes silently. No success toast.
 * Error retried twice; hard fail on third attempt (caller can watch query error state).
 */
export function useUserPreference<T>(
  supabase: SupabaseClientLike,
  key: string,
  defaultValue: T,
): [T, (v: T) => void] {
  const qc = useQueryClient();
  const { data: allPrefs } = useQuery({
    queryKey: PREF_KEYS,
    queryFn: () => fetchPreferences(supabase),
    staleTime: 5 * 60 * 1000,
  });

  const stored = allPrefs?.[key] as T | undefined;
  const current = stored !== undefined ? stored : defaultValue;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryRef = useRef(0);

  const setValue = useCallback(
    (v: T) => {
      // Optimistic update in query cache.
      qc.setQueryData(PREF_KEYS, (old: Record<string, unknown> | undefined) => ({
        ...(old ?? {}),
        [key]: v,
      }));

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        retryRef.current = 0;
        const attempt = async () => {
          try {
            await patchPreference(supabase, key, v);
            retryRef.current = 0;
          } catch {
            retryRef.current += 1;
            if (retryRef.current < 3) {
              setTimeout(() => void attempt(), 1500 * retryRef.current);
            }
          }
        };
        void attempt();
      }, 500);
    },
    [supabase, key, qc],
  );

  return [current, setValue];
}
